import { Area, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { formatMoney, formatMoneyFull } from "@/lib/money"
import { DAYS_THAT_CAN_CHANGE } from "@/lib/sales"
import { monthShort } from "./format"
import type { MonthProgressData } from "./types"

const ACTUAL_FILL = "color-mix(in srgb, var(--chart-1) 15%, transparent)"
const CAN_CHANGE_STROKE = "color-mix(in srgb, var(--chart-1) 45%, transparent)"
const CAN_CHANGE_FILL = "color-mix(in srgb, var(--chart-1) 7%, transparent)"
const AXIS_TICK = { fontSize: 11, fill: "var(--chart-axis)" }

interface Point {
  day: number
  actual: number | null
  canChange: number | null
  needed: number | null
}

// Running total of the month, day by day (display only). The last
// DAYS_THAT_CAN_CHANGE days up to today sit in `canChange`; it starts one day
// earlier than those days so the two lines join.
function points(month: MonthProgressData, showNeeded: boolean): Point[] {
  const { today_day: today, days_in_month: last } = month
  const firstCanChange = Math.max(1, today - DAYS_THAT_CAN_CHANGE + 1)
  let total = 0
  return month.daily.map(({ day, actual }) => {
    total += actual ?? 0
    const sold = day <= today ? total : null
    return {
      day,
      actual: day < firstCanChange ? sold : null,
      canChange: day >= firstCanChange - 1 && day <= today ? sold : null,
      needed: !showNeeded ? null : day === today ? month.actual : day === last ? month.plan : null,
    }
  })
}

function ticks(last: number): number[] {
  const out = [1]
  for (let d = 5; d < last; d += 5) out.push(d)
  out.push(last)
  return out
}

export default function MonthProgressChart({ currency, month }: { currency: string | null; month: MonthProgressData }) {
  const { plan, today_day: today, days_in_month: last } = month
  // The line to the plan only means something while the plan is still ahead.
  const showNeeded = plan != null && month.actual < plan && today < last
  const short = monthShort(month.month)
  return (
    <ResponsiveContainer width="100%" height={220}>
      <ComposedChart data={points(month, showNeeded)} margin={{ top: 16, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
        <XAxis dataKey="day" type="number" domain={[1, last]} ticks={ticks(last)} tick={AXIS_TICK} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={(v) => formatMoney(Number(v), currency)} tick={AXIS_TICK} axisLine={false} tickLine={false} width={64} />
        <Tooltip
          formatter={(v) => (v == null ? "-" : formatMoneyFull(Number(v), currency))}
          labelFormatter={(l) => `${l} ${short}`}
          cursor={{ stroke: "var(--chart-grid)" }}
          contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "var(--border)" }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" iconSize={10} />
        <ReferenceLine
          x={today}
          stroke="var(--chart-axis)"
          strokeDasharray="3 3"
          label={{ value: "Today", position: "top", fontSize: 11, fill: "var(--chart-axis)" }}
        />
        {plan != null && (
          <ReferenceLine
            y={plan}
            stroke="var(--chart-muted)"
            ifOverflow="extendDomain"
            label={{ value: "Plan", position: "insideTopLeft", fontSize: 11, fill: "var(--chart-axis)" }}
          />
        )}
        <Area dataKey="actual" name="Actual" type="linear" stroke="var(--chart-1)" strokeWidth={2} fill={ACTUAL_FILL} dot={false} isAnimationActive={false} />
        <Area dataKey="canChange" name="Can still change" type="linear" stroke={CAN_CHANGE_STROKE} strokeWidth={2} fill={CAN_CHANGE_FILL} dot={false} isAnimationActive={false} />
        {showNeeded && (
          <Line dataKey="needed" name="Needed" type="linear" stroke="var(--chart-axis)" strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls isAnimationActive={false} />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  )
}
